import { Collateral, DealDetail, ServicingType } from 'api'
import { getCollateralMarket } from 'model'
import { translateEvergreenNoticePeriodToNumber } from 'model/dealUtils'
import moment from 'moment'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { createHolidayGetterSelector } from 'selectors/holidaySelectors'
import { marketDetailGetterSelector, platformPreMaturityDaysSelector } from 'selectors/platformSelectors'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { DateLike } from 'utils'
import { getDefaultEffectiveDate, getIsBeforeCutOffTime, getMaxEffectiveDate, getMinEffectiveDate } from './utils'

export function useGetEffectiveDateInfo(extendMaturityDate: boolean = false) {
  const isAdmin = useSelector(isAdminGatewayUserSelector)
  const getMarketDetail = useSelector(marketDetailGetterSelector)
  const platformPreMaturityDays = useSelector(platformPreMaturityDaysSelector)
  const getHolidays = useSelector(createHolidayGetterSelector)

  return useCallback(
    (dealDetail: DealDetail, collateral: Collateral | null | undefined = undefined) => {
      const selectedCollateral = collateral === undefined ? dealDetail.collateral : collateral
      const market = selectedCollateral ? getCollateralMarket(selectedCollateral) : undefined
      const marketDetail = getMarketDetail(market)
      const marketTradeDate = !isAdmin ? marketDetail?.tradeDate ?? null : null
      const holidays = getHolidays(dealDetail.deal.currency)?.holidays ?? []
      const evergreenNoticePeriod = dealDetail.deal.evergreenNoticePeriod
      const evergreenNoticePeriodNumber = evergreenNoticePeriod ? translateEvergreenNoticePeriodToNumber(evergreenNoticePeriod) : null

      return getEffectiveDateInfo(
        dealDetail,
        selectedCollateral,
        marketTradeDate,
        platformPreMaturityDays,
        holidays,
        extendMaturityDate,
        !isAdmin && dealDetail?.deal?.servicingDetails?.servicingType?.servicingType === ServicingType.TERMINATION
          ? evergreenNoticePeriodNumber
          : null
      )
    },
    [extendMaturityDate, getHolidays, getMarketDetail, isAdmin, platformPreMaturityDays]
  )
}

export function useDealDetailsEffectiveDateInfo(dealDetails: DealDetail[], extendMaturityDate: boolean = false) {
  const getEffectiveDateInfo = useGetEffectiveDateInfo(extendMaturityDate)

  const [defaultEffectiveDate, minEffectiveDate, maxEffectiveDate, isBeforeCutOffTime] = dealDetails
    .map(d => getEffectiveDateInfo(d))
    .reduce(
      (acc, [dealDefaultEffectiveDate, dealMinEffectiveDate, dealMaxEffectiveDate, dealIsBeforeCutOffTime]) => {
        if (!acc[0] || (dealDefaultEffectiveDate && moment(dealDefaultEffectiveDate).isBefore(acc[0]))) {
          acc[0] = dealDefaultEffectiveDate
        }

        if (!acc[1] || (dealMinEffectiveDate && moment(dealMinEffectiveDate).isAfter(acc[1]))) {
          acc[1] = dealMinEffectiveDate
        }

        if (!acc[2] || (dealMaxEffectiveDate && moment(dealMaxEffectiveDate).isBefore(acc[2]))) {
          acc[2] = dealMaxEffectiveDate
        }

        if (acc[3] && !dealIsBeforeCutOffTime) {
          acc[3] = false
        }

        return acc
      },
      [null, null, null, true] as [Date | null, Date | null, Date | null, boolean]
    )

  return {
    defaultEffectiveDate,
    minEffectiveDate,
    maxEffectiveDate,
    isBeforeCutOffTime
  }
}

function getEffectiveDateInfo(
  dealDetail: DealDetail,
  collateral: Collateral | null,
  platformTradeDate: DateLike | null,
  platformPreMaturityDays: number | null,
  holidays: DateLike[],
  extendMaturityDate: boolean = false,
  evergreenNoticePeriod?: number | null
): [Date | null, Date | null, Date | null, boolean] {
  const { deal, productSector } = dealDetail

  const isBeforeCutOffTime =
    !productSector?.defaultNotificationDeadlineTime || getIsBeforeCutOffTime(productSector.defaultNotificationDeadlineTime)

  const minEffectiveDate = getMinEffectiveDate(dealDetail.deal, platformTradeDate, isBeforeCutOffTime, evergreenNoticePeriod)
  const maxEffectiveDate = getMaxEffectiveDate(dealDetail, collateral, platformPreMaturityDays, holidays, extendMaturityDate)

  const defaultEffectiveDate = platformTradeDate
    ? getDefaultEffectiveDate(platformTradeDate, isBeforeCutOffTime, deal.noticePeriodDays ?? 0, holidays, maxEffectiveDate)
    : null

  return [defaultEffectiveDate, minEffectiveDate, maxEffectiveDate, isBeforeCutOffTime]
}
